import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createPaymentConfigRule,
  createPaymentConfiguration,
  createProduct,
  createProductConfigMappings,
  deactivatePaymentConfigRule,
  deletePaymentConfigRule,
  deletePaymentConfiguration,
  deleteProduct,
  deleteAllConfigsByProductId,
  deleteSingleConfigMapping,
  getAllPaymentConfigRules,
  getAllPaymentConfigurations,
  getAllProducts,
  getConfigsByProductId,
  getPaymentConfigRulesByListingType,
  getPaymentConfigurationById,
  getProductById,
  updatePaymentConfigRule,
  updatePaymentConfiguration,
  updateProduct,
  userPaymentConfigurations,
} from "../../Services/Admin/Platform";

// ─── Products ────────────────────────────────────────────────────────────────
export const fetchUserPaymentConfig = createAsyncThunk(
  "platform/fetchUserPaymentConfig",
  async ({ role, entityId, companyId }, { rejectWithValue }) => {
    const res = await userPaymentConfigurations({ role, entityId, companyId });
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to fetch user payment config");
  },
);

export const fetchAllProducts = createAsyncThunk(
  "platform/fetchAllProducts",
  async (_, { rejectWithValue }) => {
    const res = await getAllProducts();
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to fetch products");
  },
);

export const fetchProductById = createAsyncThunk(
  "platform/fetchProductById",
  async (id, { rejectWithValue }) => {
    const res = await getProductById(id);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to fetch product");
  },
);

export const addProduct = createAsyncThunk(
  "platform/addProduct",
  async (payload, { rejectWithValue }) => {
    const res = await createProduct(payload);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to create product");
  },
);

export const editProduct = createAsyncThunk(
  "platform/editProduct",
  async (payload, { rejectWithValue }) => {
    const res = await updateProduct(payload);
    if (res?.data) return res.data.data ?? payload;
    return rejectWithValue("Failed to update product");
  },
);

export const removeProduct = createAsyncThunk(
  "platform/removeProduct",
  async (id, { rejectWithValue }) => {
    const res = await deleteProduct(id);
    if (res?.status === 200 || res?.data?.code === 200) return id;
    return rejectWithValue("Failed to delete product");
  },
);

// ─── Payment Configurations ──────────────────────────────────────────────────
export const fetchAllPaymentConfigurations = createAsyncThunk(
  "platform/fetchAllPaymentConfigurations",
  async (_, { rejectWithValue }) => {
    const res = await getAllPaymentConfigurations();
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(
      res?.data?.error || "Failed to fetch configurations",
    );
  },
);

export const fetchPaymentConfigurationById = createAsyncThunk(
  "platform/fetchPaymentConfigurationById",
  async (id, { rejectWithValue }) => {
    const res = await getPaymentConfigurationById(id);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to fetch configuration");
  },
);

export const addPaymentConfiguration = createAsyncThunk(
  "platform/addPaymentConfiguration",
  async (payload, { rejectWithValue }) => {
    const res = await createPaymentConfiguration(payload);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(
      res?.data?.error || "Failed to create configuration",
    );
  },
);

export const editPaymentConfiguration = createAsyncThunk(
  "platform/editPaymentConfiguration",
  async (payload, { rejectWithValue }) => {
    const res = await updatePaymentConfiguration(payload);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(
      res?.data?.error || "Failed to update configuration",
    );
  },
);

export const removePaymentConfiguration = createAsyncThunk(
  "platform/removePaymentConfiguration",
  async (id, { rejectWithValue }) => {
    const res = await deletePaymentConfiguration(id);
    if (res?.status === 200 || res?.data?.code === 200) return id;
    return rejectWithValue("Failed to delete configuration");
  },
);

// ─── Payment Config Rules ─────────────────────────────────────────────────────
export const fetchAllPaymentConfigRules = createAsyncThunk(
  "platform/fetchAllPaymentConfigRules",
  async (_, { rejectWithValue }) => {
    const res = await getAllPaymentConfigRules();
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to fetch config rules");
  },
);

export const fetchPaymentConfigRulesByListingType = createAsyncThunk(
  "platform/fetchPaymentConfigRulesByListingType",
  async (listingType, { rejectWithValue }) => {
    const res = await getPaymentConfigRulesByListingType(listingType);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(
      res?.data?.error || "Failed to fetch rules by listing type",
    );
  },
);

export const addPaymentConfigRules = createAsyncThunk(
  "platform/addPaymentConfigRules",
  async (payload, { rejectWithValue }) => {
    const res = await createPaymentConfigRule(payload);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to create config rules");
  },
);

export const editPaymentConfigRule = createAsyncThunk(
  "platform/editPaymentConfigRule",
  async (payload, { rejectWithValue }) => {
    const res = await updatePaymentConfigRule(payload);
    if (res?.data?.code === 200) return res.data.data;
    return rejectWithValue(res?.data?.error || "Failed to update config rule");
  },
);

export const deactivateRule = createAsyncThunk(
  "platform/deactivateRule",
  async (id, { rejectWithValue }) => {
    const res = await deactivatePaymentConfigRule(id);
    if (res?.data?.code === 200) return { id, data: res.data.data };
    return rejectWithValue(res?.data?.error || "Failed to deactivate rule");
  },
);

export const removePaymentConfigRule = createAsyncThunk(
  "platform/removePaymentConfigRule",
  async (id, { rejectWithValue }) => {
    const res = await deletePaymentConfigRule(id);
    if (res?.status === 200 || res?.data?.code === 200) return id;
    return rejectWithValue("Failed to delete config rule");
  },
);

// ─── Product Configuration Mappings ─────────────────────────────────────────
export const fetchMappingsByProduct = createAsyncThunk(
  "platform/fetchMappingsByProduct",
  async (productId, { rejectWithValue }) => {
    const res = await getConfigsByProductId(productId);
    if (res?.data?.code === 200)
      return { productId, data: res.data.data?.configurations || [] };
    return rejectWithValue(res?.data?.error || "Failed to fetch mappings");
  },
);

export const saveMappings = createAsyncThunk(
  "platform/saveMappings",
  async ({ productId, payload }, { rejectWithValue }) => {
    const res = await createProductConfigMappings(productId, payload);
    if (res?.data?.code === 200)
      return { productId, data: res.data.data?.configurations || [] };
    return rejectWithValue(res?.data?.error || "Failed to save mappings");
  },
);

export const removeAllMappings = createAsyncThunk(
  "platform/removeAllMappings",
  async (productId, { rejectWithValue }) => {
    const res = await deleteAllConfigsByProductId(productId);
    if (res?.status === 200 || res?.data?.code === 200) return productId;
    return rejectWithValue("Failed to delete mappings");
  },
);

export const removeSingleMapping = createAsyncThunk(
  "platform/removeSingleMapping",
  async ({ productId, payload }, { rejectWithValue }) => {
    const res = await deleteSingleConfigMapping(productId, payload);
    if (res?.status === 200 || res?.data?.code === 200)
      return { productId, payload };
    return rejectWithValue("Failed to delete mapping");
  },
);

// ─── Helper ───────────────────────────────────────────────────────────────────
const addAsyncCases = (builder, thunk, onFulfilled) => {
  builder
    .addCase(thunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.loading = false;
      onFulfilled(state, action);
    })
    .addCase(thunk.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.payload || action.error?.message || "Something went wrong";
    });
};

// ─── Slice ────────────────────────────────────────────────────────────────────
const platformSlice = createSlice({
  name: "platform",
  initialState: {
    userPaymentConfigs: [],
    products: [],
    selectedProduct: null,
    paymentConfigurations: [],
    selectedPaymentConfiguration: null,
    paymentConfigRules: [],
    paymentConfigRulesByListingType: [],
    mappings: {},
    wizard: {
      step: 0,
      productId: null,
      configurationId: null,
    },
    loading: false,
    error: null,
  },
  reducers: {
    setWizardStep(state, action) {
      state.wizard.step = action.payload;
    },
    setWizardProductId(state, action) {
      state.wizard.productId = action.payload;
    },
    setWizardConfigurationId(state, action) {
      state.wizard.configurationId = action.payload;
    },
    resetWizard(state) {
      state.wizard = { step: 0, productId: null, configurationId: null };
    },
    clearPlatformError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Products
    addAsyncCases(builder, fetchUserPaymentConfig, (state, { payload }) => {
      state.userPaymentConfigs = payload || [];
    });
    addAsyncCases(builder, fetchAllProducts, (state, { payload }) => {
      state.products = payload || [];
    });
    addAsyncCases(builder, fetchProductById, (state, { payload }) => {
      state.selectedProduct = payload;
    });
    addAsyncCases(builder, addProduct, (state, { payload }) => {
      if (payload) state.products.push(payload);
    });
    addAsyncCases(builder, editProduct, (state, { payload }) => {
      if (payload) {
        const i = state.products.findIndex((p) => p.id === payload.id);
        if (i !== -1) state.products[i] = payload;
      }
    });
    addAsyncCases(builder, removeProduct, (state, { payload }) => {
      state.products = state.products.filter((p) => p.id !== payload);
    });

    // Payment Configurations
    addAsyncCases(
      builder,
      fetchAllPaymentConfigurations,
      (state, { payload }) => {
        state.paymentConfigurations = payload || [];
      },
    );
    addAsyncCases(
      builder,
      fetchPaymentConfigurationById,
      (state, { payload }) => {
        state.selectedPaymentConfiguration = payload;
      },
    );
    addAsyncCases(builder, addPaymentConfiguration, (state, { payload }) => {
      if (payload) state.paymentConfigurations.push(payload);
    });
    addAsyncCases(builder, editPaymentConfiguration, (state, { payload }) => {
      if (payload) {
        const i = state.paymentConfigurations.findIndex(
          (c) => c.id === payload.id,
        );
        if (i !== -1) state.paymentConfigurations[i] = payload;
      }
    });
    addAsyncCases(builder, removePaymentConfiguration, (state, { payload }) => {
      state.paymentConfigurations = state.paymentConfigurations.filter(
        (c) => c.id !== payload,
      );
    });

    // Payment Config Rules
    addAsyncCases(builder, fetchAllPaymentConfigRules, (state, { payload }) => {
      state.paymentConfigRules = payload || [];
    });
    addAsyncCases(
      builder,
      fetchPaymentConfigRulesByListingType,
      (state, { payload }) => {
        state.paymentConfigRulesByListingType = payload || [];
      },
    );
    addAsyncCases(builder, addPaymentConfigRules, (state, { payload }) => {
      if (Array.isArray(payload)) state.paymentConfigRules.push(...payload);
      else if (payload) state.paymentConfigRules.push(payload);
    });
    addAsyncCases(builder, editPaymentConfigRule, (state, { payload }) => {
      if (payload) {
        const i = state.paymentConfigRules.findIndex(
          (r) => r.id === payload.id,
        );
        if (i !== -1) state.paymentConfigRules[i] = payload;
      }
    });
    addAsyncCases(builder, deactivateRule, (state, { payload }) => {
      const { id, data } = payload;
      const i = state.paymentConfigRules.findIndex((r) => r.id === id);
      if (i !== -1 && data) state.paymentConfigRules[i] = data;
    });
    addAsyncCases(builder, removePaymentConfigRule, (state, { payload }) => {
      state.paymentConfigRules = state.paymentConfigRules.filter(
        (r) => r.id !== payload,
      );
    });

    // Mappings
    addAsyncCases(builder, fetchMappingsByProduct, (state, { payload }) => {
      state.mappings[payload.productId] = payload.data || [];
    });
    addAsyncCases(builder, saveMappings, (state, { payload }) => {
      state.mappings[payload.productId] = payload.data || [];
    });
    addAsyncCases(builder, removeAllMappings, (state, { payload }) => {
      delete state.mappings[payload];
    });
    addAsyncCases(builder, removeSingleMapping, (state, { payload }) => {
      const { productId, payload: body } = payload;
      const list = state.mappings[productId];
      if (list) {
        state.mappings[productId] = list.filter(
          (m) => m.configurationId !== body.configurationId,
        );
      }
    });
  },
});

export const {
  setWizardStep,
  setWizardProductId,
  setWizardConfigurationId,
  resetWizard,
  clearPlatformError,
} = platformSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────
export const selectUserPaymentConfigs = (state) => state.platformSlice.userPaymentConfigs;
export const selectProducts = (state) => state.platformSlice.products;
export const selectPaymentConfigurations = (state) =>
  state.platformSlice.paymentConfigurations;
export const selectPaymentConfigRules = (state) =>
  state.platformSlice.paymentConfigRules;
export const selectPaymentConfigRulesByListingType = (state) =>
  state.platformSlice.paymentConfigRulesByListingType;
export const selectMappings = (state) => state.platformSlice.mappings;
export const selectMappingsByProduct = (productId) => (state) =>
  state.platformSlice.mappings[productId] || [];
export const selectPlatformLoading = (state) => state.platformSlice.loading;
export const selectPlatformError = (state) => state.platformSlice.error;
export const selectWizard = (state) => state.platformSlice.wizard;

export default platformSlice.reducer;
